/**
 * Thin wrapper around the shared `XSnackbar` chemistry component.
 *
 * Keeps the legacy `showToast({...})` / `toast.success/error/info/warning(...)`
 * API used throughout the SDR UI, but renders via the central
 * `@adornis/chemistry` snackbar instead of a bespoke daisyUI toast container.
 */

import { html } from 'lit';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastOptions {
  message: string;
  type?: ToastType;
  /** Milliseconds before auto-dismiss. Default 4000 (errors: 6000). */
  duration?: number;
}

const ICONS: Record<ToastType, string> = {
  success: 'icon-[material-symbols--check-circle]',
  error: 'icon-[material-symbols--error]',
  info: 'icon-[material-symbols--info]',
  warning: 'icon-[material-symbols--warning]',
};

const COLORS: Record<ToastType, string> = {
  success: 'color: var(--color-success, #16a34a);',
  error: 'color: var(--color-error, #dc2626);',
  info: 'color: var(--color-info, #2563eb);',
  warning: 'color: var(--color-warning, #d97706);',
};

export function showToast(opts: ToastOptions): void {
  const { message, type = 'info', duration = 4000 } = opts;
  const content = html`
    <span style="display:inline-flex; align-items:center; gap:0.5rem; ${COLORS[type]}">
      <span class=${ICONS[type]} style="font-size:1.125rem"></span>
      <span>${message}</span>
    </span>
  `;
  // XSnackbar.show(content, durationMS, minDurationMS)
  void XSnackbar.show(content, duration, Math.min(1000, duration));
}

export const toast = {
  success: (message: string, duration?: number) => showToast({ message, type: 'success', duration }),
  error: (message: string, duration?: number) => showToast({ message, type: 'error', duration: duration ?? 6000 }),
  info: (message: string, duration?: number) => showToast({ message, type: 'info', duration }),
  warning: (message: string, duration?: number) => showToast({ message, type: 'warning', duration }),
};
